
import React, { createContext, useState, useContext, ReactNode } from "react";

export type BoatType = {
  id: string;
  name: string;
  model: string;
  year: number;
  length: number;  // en mètres
  width: number;   // en mètres
  draft: number;   // en mètres
  engine: string;
  engineHours?: number;
  waterCapacity: number;  // en litres
  fuelCapacity: number;   // en litres
  customFields?: Record<string, string>;
  image?: string;
};

type BoatContextType = {
  boats: BoatType[];
  selectedBoat: BoatType | null;
  setSelectedBoat: (boat: BoatType) => void;
  addBoat: (boat: BoatType) => void;
  removeBoat: (id: string) => void;
  updateBoat: (id: string, data: Partial<BoatType>) => void;
};

const BoatContext = createContext<BoatContextType | undefined>(undefined);

// Sample initial boat data
const sampleBoats: BoatType[] = [
  {
    id: "1",
    name: "Sea Explorer",
    model: "Oceanic 42",
    year: 2020,
    length: 12.8,  // Converti en mètres
    width: 4.3,    // Converti en mètres
    draft: 1.8,    // Converti en mètres
    engine: "Twin Volvo Penta D6-400",
    engineHours: 450,
    waterCapacity: 568,  // Converti en litres
    fuelCapacity: 1438,  // Converti en litres
    image: "/lovable-uploads/boat1.jpg",
  },
  {
    id: "2",
    name: "Wind Dancer",
    model: "SailMaster 38",
    year: 2018,
    length: 11.6,  // Converti en mètres
    width: 3.7,    // Converti en mètres
    draft: 1.7,    // Converti en mètres
    engine: "Yanmar 4JH5E",
    engineHours: 320,
    waterCapacity: 454,  // Converti en litres
    fuelCapacity: 757,   // Converti en litres
    image: "/lovable-uploads/boat2.jpg",
  },
  {
    id: "3",
    name: "Blue Wave",
    model: "Navigator 45",
    year: 2021,
    length: 13.7,
    width: 4.5,
    draft: 1.9,
    engine: "Caterpillar C12.9 1000hp",
    engineHours: 150,
    waterCapacity: 750,
    fuelCapacity: 1800,
    image: "/lovable-uploads/boat3.jpg",
  },
  {
    id: "4",
    name: "Océan Libre",
    model: "Bénéteau Oceanis 40",
    year: 2019,
    length: 12.2,
    width: 4.0,
    draft: 1.7,
    engine: "Volvo Penta D2-40",
    engineHours: 280,
    waterCapacity: 420,
    fuelCapacity: 600,
    image: "/lovable-uploads/boat4.jpg",
  },
  {
    id: "5",
    name: "Étoile Marine",
    model: "Dufour Grand Large 460",
    year: 2020,
    length: 14.0,
    width: 4.5,
    draft: 2.1,
    engine: "Volvo Penta D2-75",
    engineHours: 210,
    waterCapacity: 580,
    fuelCapacity: 480,
    image: "/lovable-uploads/boat5.jpg",
  },
];

export const BoatProvider = ({ children }: { children: ReactNode }) => {
  const [boats, setBoats] = useState<BoatType[]>(sampleBoats);
  const [selectedBoat, setSelectedBoat] = useState<BoatType | null>(boats[0]);

  const addBoat = (boat: BoatType) => {
    setBoats([...boats, boat]);
  };

  const removeBoat = (id: string) => {
    const newBoats = boats.filter(boat => boat.id !== id);
    setBoats(newBoats);
    
    // If the selected boat was removed, select the first available boat
    if (selectedBoat && selectedBoat.id === id) {
      setSelectedBoat(newBoats.length > 0 ? newBoats[0] : null);
    }
  };

  const updateBoat = (id: string, data: Partial<BoatType>) => {
    const newBoats = boats.map(boat => 
      boat.id === id ? { ...boat, ...data } : boat
    );
    setBoats(newBoats);
    
    // Update selected boat if it was the one modified
    if (selectedBoat && selectedBoat.id === id) {
      const updatedBoat = newBoats.find(boat => boat.id === id);
      if (updatedBoat) setSelectedBoat(updatedBoat);
    }
  };

  return (
    <BoatContext.Provider value={{ 
      boats, 
      selectedBoat, 
      setSelectedBoat, 
      addBoat, 
      removeBoat,
      updateBoat
    }}>
      {children}
    </BoatContext.Provider>
  );
};

export const useBoat = () => {
  const context = useContext(BoatContext);
  if (context === undefined) {
    throw new Error('useBoat must be used within a BoatProvider');
  }
  return context;
};
